import { seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import { GET_EVIDENCE_AWARENESS_POPUP_VARIANT } from '@/components/constants/flags';
import { useManualExposure } from '@/hooks/amplitude';
import { useMutateMarkModalAsViewed } from '@/hooks/mutation/useMutateMarkModalAsViewed';
import {
  PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY,
  useQueryPersonalizedEvidenceSuggestions,
} from '@/hooks/query/useQueryPersonalizedEvidenceSuggestions';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { track } from '@/lib/analytics/track';
import { SESSIONLESS_EVIDENCE_AWARENESS_AUTO_DISMISS_MS } from '@/lib/modalClosedRules';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import {
  ModalId,
  ModalViewAction,
  PersonalizedEvidenceSuggestionCategory,
} from '@heidi/schema/ml-scribe';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSessionlessEvidenceAwarenessEligibility } from './useSessionlessEvidenceAwarenessEligibility';
import { useSessionlessEvidenceAwarenessPresentationSuppressed } from './useSessionlessEvidenceAwarenessPresentationSuppressed';

/**
 * How the popover closed — drives BE counters:
 *   - `engage`   → CTA click; bumps `engage_count`, resets dismiss streak.
 *   - `dismiss`  → user X, Escape, or 5-min auto-dismiss (outside click ignored); bumps the
 *     dismiss streak that feeds the long-suppression rule.
 *   - `view`     → eligible + fetched but no renderable content; records a
 *     passive impression so the 4-day cadence cooldown anchors.
 *   - `suppress` → hidden without counting as ignored: competing banner-only
 *     surface covered it while invisible, OR brand-new-user grace period (no
 *     record yet). Updates `last_seen_at` only — no `view_count` bump, no
 *     dismiss-streak bump. Transcription workflow after the popover was visible
 *     uses `dismiss` instead (see presentation-suppressed effect).
 */
type FinalizeIntent = 'engage' | 'dismiss' | 'view' | 'suppress';

export type UseSessionlessEvidenceAwarenessPopoverResult = {
  showPopover: boolean;
  title: string;
  suggestion: string;
  ctaButtonLabel: string;
  category: PersonalizedEvidenceSuggestionCategory | null | undefined;
  onDismiss: () => void;
  onCta: () => void;
  handleOpenChange: (open: boolean) => void;
};

/**
 * Single orchestrator for the sessionless Evidence awareness popover. Composes the
 * eligibility and presentation-suppressed gates with all popover-specific state,
 * impression/engagement tracking, and auto-dismiss so the component only needs one
 * hook call and stays a thin renderer.
 */
export function useSessionlessEvidenceAwarenessPopover(): UseSessionlessEvidenceAwarenessPopoverResult {
  const router = useRouter();
  const intl = useIntl();
  useAtomValue(sessionlessEvidenceAwarenessDevVariantOverrideAtom);
  const devForceShow = useAtomValue(sessionlessEvidenceAwarenessDevForceShowAtom);
  const isEligible = useSessionlessEvidenceAwarenessEligibility();
  const devForceShowActive = getEnvironment() !== ENV_NAME.PROD && devForceShow;
  const presentationSuppressionRaw =
    useSessionlessEvidenceAwarenessPresentationSuppressed();
  const presentationSuppressed =
    !devForceShowActive && presentationSuppressionRaw.isSuppressed;
  const transcriptionWorkflowSuppressed =
    !devForceShowActive && presentationSuppressionRaw.isTranscriptionWorkflowSuppressed;
  const { data: existingModalView } = useQueryUser(
    (u) => u?.seen_modals?.[ModalId.SESSIONLESS_EVIDENCE_AWARENESS]
  );
  // No record means the cadence rule has nothing to anchor against, so the
  // popover would show on first login. Anchor a 4-day cooldown via a passive
  // `suppress` record below. Dev force-show bypasses this for previewing.
  const isFirstImpressionGracePeriod = !devForceShowActive && existingModalView == null;
  // Skip the suggestion fetch during the grace period — the response would
  // be discarded since the popover doesn't render.
  const fetchSuggestions =
    (isEligible && !isFirstImpressionGracePeriod) || devForceShowActive;
  const queryClient = useQueryClient();
  const setSeenModalIds = useSetAtom(seenModalIdsAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();

  /** Prevents double-finalize per impression; cleared when eligibility allows another show. */
  const finalizedIntentRef = useRef<FinalizeIntent | null>(null);
  /** True once `showPopover` was true this eligibility cycle — resets with `finalizedIntentRef`. */
  const sawPopoverThisCycleRef = useRef(false);
  /** Prevents duplicate ModalViewed per impression; resets with `sawPopoverThisCycleRef`. */
  const hasTrackedImpressionRef = useRef(false);
  /** Prior `(isEligible || devForceShowActive)` — ref guard resets only on transition into true so Strict Mode does not clear after grace `finalize`. */
  const prevFinalizeResetGateRef = useRef<boolean | null>(null);

  const finalize = useCallback(
    (intent: FinalizeIntent) => {
      if (finalizedIntentRef.current) return;
      finalizedIntentRef.current = intent;

      setSeenModalIds(
        (prev) => new Set([...(prev ?? []), ModalId.SESSIONLESS_EVIDENCE_AWARENESS])
      );

      const action =
        intent === 'engage'
          ? ModalViewAction.engage
          : intent === 'dismiss'
            ? ModalViewAction.dismiss
            : undefined;
      markModalAsViewed({
        modal_id: ModalId.SESSIONLESS_EVIDENCE_AWARENESS,
        increment_count: intent === 'view',
        ...(action != null ? { action } : {}),
      });
      void queryClient.invalidateQueries({
        queryKey: PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY,
      });
    },
    [markModalAsViewed, queryClient, setSeenModalIds]
  );

  const {
    data: personalizedSuggestion,
    isSuccess,
    isFetched,
    isFetching,
  } = useQueryPersonalizedEvidenceSuggestions({
    enabled: fetchSuggestions,
  });

  const hasRenderableSuggestions =
    isSuccess &&
    personalizedSuggestion != null &&
    personalizedSuggestion.suggestion != null;

  const showDevForceEmptyApiPreview =
    devForceShowActive && isFetched && !hasRenderableSuggestions;

  const showPopover =
    fetchSuggestions &&
    !presentationSuppressed &&
    !isFirstImpressionGracePeriod &&
    !isFetching &&
    (hasRenderableSuggestions || showDevForceEmptyApiPreview);

  const title = useMemo(() => {
    if (personalizedSuggestion?.title) return personalizedSuggestion.title;
    switch (personalizedSuggestion?.category) {
      case PersonalizedEvidenceSuggestionCategory.research:
        return intl.formatMessage({
          defaultMessage: 'Bridge your knowledge gaps',
          id: '0QXHRPtQ2v',
        });
      case PersonalizedEvidenceSuggestionCategory.planning:
        return intl.formatMessage({
          defaultMessage: 'Stay on top of your workflow',
          id: 'ef2EVPnYVv',
        });
      case PersonalizedEvidenceSuggestionCategory.documentation:
        return intl.formatMessage({
          defaultMessage: 'Automate your paperwork',
          id: 'SMt7J0JsPI',
        });
      case PersonalizedEvidenceSuggestionCategory.reflection:
        return intl.formatMessage({
          defaultMessage: 'Reflect on your practice',
          id: 'k9wGOay0rl',
        });
      default:
        return intl.formatMessage({
          defaultMessage: 'Level up your practice with Evidence',
          id: 'XSbKBl7Rw5',
        });
    }
  }, [personalizedSuggestion?.category, personalizedSuggestion?.title, intl]);

  const devEmptyApiPreviewSuggestion = intl.formatMessage({
    defaultMessage:
      'No personalized suggestion was returned (control or empty response). Dev preview only.',
    id: 'l2YF9f6mPV',
  });

  const evidenceAwarenessPopupVariant = GET_EVIDENCE_AWARENESS_POPUP_VARIANT();
  useManualExposure({
    flagKey: 'exp-evidence-awareness-popup',
    isEnabled: evidenceAwarenessPopupVariant !== 'control',
    isVisible: showPopover,
    variant: evidenceAwarenessPopupVariant,
  });

  const onDismiss = useCallback(() => {
    const suggestionCategory = personalizedSuggestion?.category;
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.SessionlessEvidenceAwarenessCard,
        action: ButtonAction.SessionlessEvidenceAwarenessDismissClicked,
        label: 'sessionless_evidence_awareness_dismiss',
        ...(suggestionCategory != null ? { category: suggestionCategory } : {}),
      },
    });
    finalize('dismiss');
  }, [finalize, personalizedSuggestion?.category]);

  const onCta = useCallback(() => {
    const suggestionCategory = personalizedSuggestion?.category;
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.SessionlessEvidenceAwarenessCard,
        action: ButtonAction.SessionlessEvidenceAwarenessCtaClicked,
        label: 'sessionless_evidence_awareness_open_evidence',
        ...(suggestionCategory != null ? { category: suggestionCategory } : {}),
      },
    });
    const query = personalizedSuggestion?.suggestion;
    const path = query
      ? `/scribe/chat?query=${encodeURIComponent(query)}`
      : '/scribe/chat';
    void router.push(path);
    finalize('engage');
  }, [
    router,
    finalize,
    personalizedSuggestion?.category,
    personalizedSuggestion?.suggestion,
  ]);

  useEffect(() => {
    if (!showPopover || hasTrackedImpressionRef.current) return;
    hasTrackedImpressionRef.current = true;
    const suggestionCategory = personalizedSuggestion?.category;
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.ModalViewed,
      props: {
        screen_name: ScreenName.SessionlessEvidenceAwarenessCard,
        ...(suggestionCategory != null
          ? { suggestion_category: suggestionCategory }
          : {}),
      },
    });
  }, [showPopover, personalizedSuggestion?.category]);

  useEffect(() => {
    if (showPopover) sawPopoverThisCycleRef.current = true;
  }, [showPopover]);

  // Reset finalize guard when we newly become eligible (or dev preview), then
  // anchor brand-new users with `suppress` without fetching/rendering. Single
  // effect keeps Strict Mode's second pass from clearing the ref after suppress.
  useEffect(() => {
    const finalizeResetGateOpen = isEligible || devForceShowActive;
    const prevGate = prevFinalizeResetGateRef.current;
    if (finalizeResetGateOpen && prevGate !== true) {
      finalizedIntentRef.current = null;
    }
    // Only clear after becoming eligible from an ineligible spell — not on first
    // mount (`prevGate` null), or sawPopover ref would reset before the suppress
    // effect runs in the same flush as the first visible render.
    if (finalizeResetGateOpen && prevGate === false) {
      sawPopoverThisCycleRef.current = false;
      hasTrackedImpressionRef.current = false;
    }
    prevFinalizeResetGateRef.current = finalizeResetGateOpen;

    if (devForceShowActive) return;
    if (!isEligible) return;
    if (!isFirstImpressionGracePeriod) return;
    finalize('suppress');
  }, [devForceShowActive, isEligible, isFirstImpressionGracePeriod, finalize]);

  useEffect(() => {
    if (!fetchSuggestions || !isFetched || isFetching) return;
    if (devForceShowActive) return;

    if (presentationSuppressed) {
      // Competing surface hid the popover — anchor cadence without bumping
      // `view_count` so we don't re-fetch on the next mount. If the user had
      // already seen the card and then started transcription work, count as
      // dismiss for consecutive_dismiss streaks / long suppression.
      if (hasRenderableSuggestions) {
        const countAsDismissIgnored =
          sawPopoverThisCycleRef.current && transcriptionWorkflowSuppressed;
        finalize(countAsDismissIgnored ? 'dismiss' : 'suppress');
      }
      return;
    }

    if (hasRenderableSuggestions) return;
    finalize('view');
  }, [
    devForceShowActive,
    fetchSuggestions,
    isFetched,
    isFetching,
    hasRenderableSuggestions,
    presentationSuppressed,
    transcriptionWorkflowSuppressed,
    finalize,
  ]);

  useEffect(() => {
    if (!showPopover) return;
    const timeoutId = setTimeout(() => {
      finalize('dismiss');
    }, SESSIONLESS_EVIDENCE_AWARENESS_AUTO_DISMISS_MS);
    return () => clearTimeout(timeoutId);
  }, [showPopover, finalize]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) finalize('dismiss');
    },
    [finalize]
  );

  return {
    showPopover,
    title,
    suggestion: personalizedSuggestion?.suggestion ?? devEmptyApiPreviewSuggestion,
    ctaButtonLabel:
      personalizedSuggestion?.cta ??
      intl.formatMessage({
        defaultMessage: 'Open Evidence',
        id: 'fPDGPxDzjp',
      }),
    category: personalizedSuggestion?.category,
    onDismiss,
    onCta,
    handleOpenChange,
  };
}
